import { CircleCheck, Loader2, RefreshCw, ShieldCheck, Unplug } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../Shared/Button/Button';
import { OAuthPhase } from '@/Domain/hooks/Action/RemoteMcp/useRemoteMcpOAuth.hook';

type Props = {
  phase: OAuthPhase;
  providerLabel: string;
  connectedStatus?: 'needs_authorization' | 'finalizing' | 'authorized' | 'error';
  onConnect: () => void;
  onReconnect: () => void;
  onDisconnect: () => void;
  onCancel: () => void;
  isDisconnecting: boolean;
};

export const RemoteMcpOAuthStatus = ({
  phase,
  providerLabel,
  connectedStatus,
  onConnect,
  onReconnect,
  onDisconnect,
  onCancel,
  isDisconnecting,
}: Props) => {
  const { t } = useTranslation('remote_mcp');
  const provider = providerLabel || '';
  const displayedPhase =
    connectedStatus === 'finalizing' && phase === 'idle' ? 'finalizing' : phase;

  if (displayedPhase === 'authorizing' || displayedPhase === 'finalizing') {
    return (
      <div
        className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card p-6 text-center"
        data-testid={`remote-mcp-oauth-${displayedPhase}`}
      >
        <span className="flex size-10 items-center justify-center rounded-full bg-info-muted text-info">
          <Loader2 className="size-5 animate-spin" />
        </span>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-foreground">
            {displayedPhase === 'authorizing'
              ? t('oauth.authorizing_title', { provider })
              : t('oauth.finalizing_title')}
          </p>
          <p className="max-w-sm text-sm text-muted-foreground">
            {displayedPhase === 'authorizing' ? t('oauth.authorizing') : t('oauth.finalizing')}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isDisconnecting}
          data-testid="remote-mcp-oauth-cancel"
        >
          {t('button.cancel')}
        </Button>
      </div>
    );
  }

  if (connectedStatus === 'authorized' && phase === 'idle') {
    return (
      <div
        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4"
        data-testid="remote-mcp-oauth-connected"
      >
        <span className="flex items-center gap-2.5 text-sm font-medium text-foreground">
          <span className="flex size-8 items-center justify-center rounded-full bg-success-muted text-success">
            <CircleCheck className="size-4" />
          </span>
          {t('oauth.connected', { provider })}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={onReconnect}
            disabled={isDisconnecting}
            data-testid="remote-mcp-oauth-reconnect"
          >
            <RefreshCw className="size-4" />
            {t('oauth.reconnect')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDisconnect}
            disabled={isDisconnecting}
            data-testid="remote-mcp-oauth-disconnect"
            className="text-destructive hover:text-destructive hover:bg-destructive-muted"
          >
            <Unplug className="size-4" />
            {t('oauth.disconnect')}
          </Button>
        </div>
      </div>
    );
  }

  if (
    phase === 'pre_consent' ||
    connectedStatus === 'needs_authorization' ||
    connectedStatus === 'error'
  ) {
    return (
      <div
        className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5"
        data-testid="remote-mcp-oauth-pre-consent"
      >
        <div className="flex items-start gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-info-muted text-info">
            <ShieldCheck className="size-5" />
          </span>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold text-foreground">{t('oauth.pre_consent_title')}</p>
            <p className="text-sm text-muted-foreground">
              {t('oauth.pre_consent_description', { provider })}
            </p>
          </div>
        </div>
        <Button
          variant="dark"
          className="w-full"
          onClick={onConnect}
          data-testid="remote-mcp-oauth-connect-button"
        >
          <ShieldCheck className="size-4" />
          {t('oauth.connect_with_provider', { provider })}
        </Button>
        {!connectedStatus && (
          <p className="text-center text-xs text-foreground-subtle">
            {t('oauth.pre_consent_hint')}
          </p>
        )}
      </div>
    );
  }

  return null;
};
